import api from './client';
import type { Blahblah, PaginatedResponse, FeedType } from '@/types';
import { transformBlahblah, transformPaginatedResponse } from '@/lib/utils';

export async function getFeed(
  type: FeedType = 'all',
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<Blahblah>> {
  const endpoint = type === 'following' ? '/feed/following' : '/feed';
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', limit.toString());

  const { data } = await api.get(`${endpoint}?${params.toString()}`);
  return transformPaginatedResponse(data, transformBlahblah);
}

export async function getUserBlahblahs(
  userId: string,
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<Blahblah>> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', limit.toString());

  const { data } = await api.get(`/users/${userId}/blahblahs?${params.toString()}`);
  return transformPaginatedResponse(data, transformBlahblah);
}

export async function getBlahblah(id: string): Promise<Blahblah> {
  const { data } = await api.get(`/blahblahs/${id}`);
  return transformBlahblah(data) as Blahblah;
}

export async function getReplies(
  blahblahId: string,
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<Blahblah>> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', limit.toString());

  const { data } = await api.get(`/blahblahs/${blahblahId}/replies?${params.toString()}`);
  return transformPaginatedResponse(data, transformBlahblah);
}

export async function createBlahblah(
  audioBlob: Blob,
  durationMs: number,
  options?: {
    parentId?: string;
    language?: string;
  }
): Promise<Blahblah> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'audio.webm');
  formData.append('duration_ms', durationMs.toString());

  if (options?.parentId) {
    formData.append('parent_id', options.parentId);
  }
  if (options?.language) {
    formData.append('language', options.language);
  }

  const { data } = await api.post('/blahblahs', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return transformBlahblah(data) as Blahblah;
}

export async function deleteBlahblah(id: string): Promise<void> {
  await api.delete(`/blahblahs/${id}`);
}

export async function likeBlahblah(id: string): Promise<{ likesCount: number; isLiked: boolean }> {
  const { data } = await api.post(`/blahblahs/${id}/like`);
  return {
    likesCount: data.likes_count ?? data.likesCount,
    isLiked: data.is_liked ?? data.isLiked,
  };
}

export async function unlikeBlahblah(id: string): Promise<{ likesCount: number; isLiked: boolean }> {
  const { data } = await api.delete(`/blahblahs/${id}/like`);
  return {
    likesCount: data.likes_count ?? data.likesCount,
    isLiked: data.is_liked ?? data.isLiked,
  };
}

export async function recordView(id: string): Promise<{ viewsCount: number }> {
  const { data } = await api.post(`/blahblahs/${id}/view`);
  return {
    viewsCount: data.views_count ?? data.viewsCount,
  };
}

export async function reportBlahblah(id: string, reason: string): Promise<void> {
  await api.post(`/blahblahs/${id}/report`, { reason });
}

export async function favoriteBlahblah(id: string): Promise<{ isFavorited: boolean }> {
  const { data } = await api.post(`/blahblahs/${id}/favorite`);
  return {
    isFavorited: data.is_favorited ?? data.isFavorited ?? true,
  };
}

export async function unfavoriteBlahblah(id: string): Promise<{ isFavorited: boolean }> {
  const { data } = await api.delete(`/blahblahs/${id}/favorite`);
  return {
    isFavorited: data.is_favorited ?? data.isFavorited ?? false,
  };
}

export async function getFavorites(
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<Blahblah>> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', limit.toString());

  const { data } = await api.get(`/users/me/favorites?${params.toString()}`);
  return transformPaginatedResponse(data, transformBlahblah);
}
